import { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Spinner, useToast } from '../../components/ui';
import {
  CycleSelector, LicenseActivationCard, PaymentMethodSelector, PixChargeCard, PlanCards,
  type BillingCycle, type PaymentMethod, type PixCharge, type PlanView
} from '../../components/PlanPicker';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { tr } from '../../lib/i18n';

interface PlansResponse {
  trialDays: number;
  paymentConfigured: boolean;
  paymentProvider: string;
  checkoutMethods: PaymentMethod[];
  pixEnabled: boolean;
  plans: PlanView[];
}

interface CheckoutResponse {
  mode: string;
  checkoutUrl?: string;
  message?: string;
  note?: string;
}

function messageOf(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : 'Não foi possível concluir a operação.';
}

const HEADING: Record<string, string> = {
  expired: 'Seu teste gratuito terminou',
  past_due: 'Pagamento pendente',
  canceled: 'Assinatura cancelada',
  suspended: 'Conta suspensa'
};

export function BillingGate(): JSX.Element {
  const toast = useToast();
  const { active, refresh } = useAuth();
  const bid = active?.businessId ?? '';
  const sub = active?.subscription ?? null;
  const isOwner = active?.role === 'owner';
  const suspended = sub?.status === 'suspended';

  const [plans, setPlans] = useState<PlanView[]>([]);
  const [trialDays, setTrialDays] = useState(7);
  const [pixEnabled, setPixEnabled] = useState(false);
  const [checkoutMethods, setCheckoutMethods] = useState<PaymentMethod[]>([]);
  const [paymentProvider, setPaymentProvider] = useState('none');
  const [, setPaymentConfigured] = useState<boolean | null>(null);
  const [pix, setPix] = useState<PixCharge | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [method, setMethod] = useState<PaymentMethod>('card');

  const loadPix = useCallback(async (businessId: string): Promise<void> => {
    try {
      setPix(await api.get<PixCharge>(`/api/subscriptions/${businessId}/pix`));
    } catch {
      setPix(null);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<PlansResponse>('/api/plans')
      .then((res) => {
        if (!alive) return;
        setPlans(res.plans);
        setTrialDays(res.trialDays);
        setPixEnabled(res.pixEnabled);
        setCheckoutMethods(res.checkoutMethods ?? []);
        setPaymentProvider(res.paymentProvider ?? 'none');
        setPaymentConfigured(res.paymentConfigured);
        if (bid) void loadPix(bid);
      })
      .catch((err: unknown) => {
        if (alive) setError(messageOf(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [bid, loadPix]);

  async function checkout(planKey: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<CheckoutResponse>(`/api/subscriptions/${bid}/checkout`, { planKey, cycle, method });
      if (res.mode === 'pix') {
        await loadPix(bid);
        toast(tr('Cobrança Pix gerada. Pague e envie o comprovante.'), 'success');
      } else if (res.mode === 'hosted_checkout' && res.checkoutUrl) {
        toast(tr('Abrindo ambiente de pagamento…'), 'info');
        window.setTimeout(() => {
          window.location.href = res.checkoutUrl as string;
        }, 900);
      } else if (res.mode === 'pending_configuration') {
        toast(tr('Pedido registrado. Cobrança online indisponível.'), 'info');
      } else {
        toast(tr('Checkout iniciado. A ativação ocorre após a confirmação do pagamento.'), 'info');
      }
      await refresh();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  const headingKey = sub ? HEADING[sub.status] : null;
  const heading = headingKey ? tr(headingKey) : tr('Escolha seu plano');
  const intro = suspended
    ? tr('O acesso deste estabelecimento foi suspenso pela administração da AgendaPro. Fale com o suporte antes de contratar um plano.')
    : sub?.status === 'past_due'
      ? tr('A cobrança pendente precisa ser paga para liberar a agenda. Ative uma licença ou escolha o ciclo e o plano abaixo para gerar a cobrança.')
      : sub?.status === 'canceled'
        ? tr('A assinatura foi cancelada e o acesso operacional está bloqueado. Ative uma licença ou escolha o ciclo de cobrança e o plano para reativar.')
        : sub
          ? tr('Os {days} dias de teste terminaram. Ative com o código de licença enviado pela administração ou escolha um plano — mensal, semestral ou anual — para liberar novamente agenda e relatórios.', { days: trialDays })
          : tr('Use o código de licença enviado pela administração ou escolha um ciclo de cobrança e um plano para liberar agenda e relatórios.');

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{heading}</h1>
        <p className="nl-hint">{intro}</p>
      </div>

      {error ? <Alert tone="danger" title={tr("Não foi possível concluir")}>{error}</Alert> : null}

      {suspended ? null : (
        <>
          {isOwner ? <LicenseActivationCard businessId={bid} onActivated={refresh} /> : null}

          {pix ? <PixChargeCard charge={pix} /> : null}

          {!isOwner ? (
            <Alert tone="info" title={tr("Somente o proprietário")}>
              {tr("Contratar um plano ou resgatar uma licença é permitido apenas ao proprietário do estabelecimento.")}
            </Alert>
          ) : null}

          <Card as="section">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">{tr("Planos")}</h2>
              <div className="flex flex-wrap items-center gap-2">
                <PaymentMethodSelector method={method} methods={checkoutMethods} onChange={setMethod} />
                <CycleSelector cycle={cycle} onChange={setCycle} />
              </div>
            </div>

            {loading ? (
              <Spinner label={tr("Carregando planos…")} />
            ) : plans.length === 0 ? (
              <p className="text-sm text-muted">{tr("Nenhum plano disponível no momento. Fale com o suporte.")}</p>
            ) : (
              <PlanCards
                plans={plans}
                cycle={cycle}
                method={method}
                checkoutMethods={checkoutMethods}
                busy={busy}
                pixEnabled={pixEnabled}
                canChoose={isOwner}
                onChoose={(planKey) => void checkout(planKey)}
              />
            )}

            {checkoutMethods.length === 0 && !pixEnabled ? (
              <p className="nl-hint mt-3">{tr("A cobrança online ainda não foi configurada pelo administrador da AgendaPro. O pedido fica pendente — nenhuma cobrança é feita e o plano não é ativado até a configuração do provedor.")}</p>
            ) : null}
            {checkoutMethods.length === 0 && pixEnabled ? (
              <p className="nl-hint mt-3">{tr("Ao clicar, o QR Code e o Pix copia e cola aparecem acima, com o valor exato do ciclo escolhido. A liberação do acesso é feita pela administração após conferir o comprovante.")}</p>
            ) : null}
            {checkoutMethods.length > 0 ? (
              <p className="nl-hint mt-3">
                {tr("Pagamento processado pelo")} {paymentProvider === 'asaas' ? 'Asaas' : paymentProvider} {tr("em ambiente seguro. A liberação do acesso é automática após a confirmação — voltar para esta página não ativa nada por si só.")}
                {method === 'card' ? tr(' No cartão, a cobrança se renova automaticamente a cada ciclo.') : tr(' No Pix, o valor vale para o ciclo escolhido e não se renova sozinho.')}
              </p>
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
}
